import ChunkManager from './ChunkManager';
import { type HashContent, parseHash } from './controller/HashManager';
import { type EventStateWithOptions, parseChunks, parseEvent } from './parse';
import type { RawEvent } from './parse';
import type { ChunkInfo, NetReplayModule } from './store';
import { type Mutable, longestCommonPrefix, queryize } from './utils';
import Option from './utils/option';

const CDN_HOST = import.meta.env.VITE_CDN_HOST;

function calculateChunkSize(startersCount: number): number {
  if (startersCount > 10000) {
    return 10 * 60 * 1000;
  }
  if (startersCount > 1000) {
    return 60 * 60 * 1000;
  }
  return 24 * 60 * 60 * 1000;
}

function getJSON<U>(url: string): Promise<U> {
  return fetch(url).then((res) => res.json());
}
async function getArrayBufferOpt(url: string): Promise<Option<ArrayBuffer>> {
  const res = await fetch(url);
  if (res.status === 200) {
    return Option.of(await res.arrayBuffer());
  }
  return Option.none();
}

function generateShortNames(events: Array<EventStateWithOptions>): Array<EventStateWithOptions> {
  const prefix = longestCommonPrefix(events.map((event) => event.shortName));
  return events.map((event) => ({
    ...event,
    shortName: event.shortName.substring(prefix.length),
  }));
}

export async function triggerPageview(eventId: string): Promise<void> {
  fetch(`${CDN_HOST}/api/events/${eventId}/loads`, {
    method: 'POST',
    body: JSON.stringify({ loadType: 'MAP' }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function getPointsBuffer(
  host: string,
  eventId: string,
  options: {
    chunkSize?: number;
    netReplay?: NetReplayModule;
    range?: [Date, Date];
    starterIds?: Array<string>;
  } = {},
): Promise<Option<ArrayBuffer>> {
  const opts: {
    chunk_size?: string;
    net_replay?: string;
    range?: string;
    starter_ids?: string;
    offset_mode?: string;
  } = { offset_mode: 'binarized' };
  if (options.chunkSize != null) {
    opts.chunk_size = String(options.chunkSize);
  }
  if (options.netReplay?.enabled) {
    opts.net_replay = `${options.netReplay.syncOffset},${options.netReplay.syncTime.toISOString()}`;
  }
  if (options.range != null) {
    opts.range = `${options.range[0].toISOString()}--${options.range[1].toISOString()}`;
  }
  if (options.starterIds != null) {
    opts.starter_ids = options.starterIds.join(',');
  }

  const url = queryize(`${host}/api/events/${eventId}/points.bin`, opts);
  return getArrayBufferOpt(url);
}

type EventDataPair = {
  eventData: EventStateWithOptions;
  chunkInfos: Array<ChunkInfo>;
};

export async function loadEventData(eventId: string): Promise<Array<EventDataPair>> {
  const hash = parseHash(window.location.hash);
  let eventInfos = await getJSON<Array<RawEvent>>(`${CDN_HOST}/api/player/${eventId}/info`);

  if (window.location.search.includes('app=true')) {
    // Reload without cache if the selected ids cannot be found in any events' starter list
    if (hash.selected != null && hash.selected.length > 0) {
      if (
        hash.selected.some(
          (selectedId) =>
            !eventInfos.some((event) =>
              event.starters.some((starter) => selectedId === starter.id),
            ),
        )
      ) {
        eventInfos = await getJSON<Array<RawEvent>>(
          `${CDN_HOST}/api/player/${eventId}/info?nocache=${hash.selected.join(',')}`,
        );
      }
    }
  }
  const chunkedShadowTracks = await Promise.all(
    eventInfos.map((event) =>
      getArrayBufferOpt(`${CDN_HOST}/api/events/${event.id}/geo/shadow.chunked.bin`),
    ),
  );
  return await Promise.all(
    eventInfos.map(async (eventInfo, i) => {
      let eventData = parseEvent(i, eventInfo, chunkedShadowTracks[i]);

      const buffer = await getPointsBuffer(CDN_HOST, eventData.eventId, {
        chunkSize: calculateChunkSize(eventData.starters.length),
        netReplay: eventData.modules.netReplay,
        range: [new Date(eventData.startTime), new Date(eventData.endTime)],
      });
      const chunkInfos = buffer.map(parseChunks).getOrElse(() => []);

      eventData = overrideWithHash(eventData, hash);

      return { eventData, chunkInfos };
    }),
  );
}

export async function loadData(eventId: string): Promise<Array<EventStateWithOptions>> {
  const eventDataPairs = await loadEventData(eventId);
  const events: Array<EventStateWithOptions> = [];
  for (const { eventData, chunkInfos } of eventDataPairs) {
    events.push({
      ...eventData,
      changeable: {
        eventId: eventData.eventId,
        chunkManager: ChunkManager.create(
          chunkInfos,
          eventData.starters,
          eventData.chunkedShadowTrack,
        ),
        starterTracks: new Map(),
      },
    });
  }
  return generateShortNames(events);
}

function overrideWithHash(
  eventData: EventStateWithOptions,
  hash: HashContent,
): EventStateWithOptions {
  const options = eventData.options as Mutable<EventStateWithOptions['options']>;
  const infoTabsOptions = options.infoTabs as Mutable<EventStateWithOptions['options']['infoTabs']>;
  if (hash.listOpen != null) {
    options.listOpenByDefault = hash.listOpen;
  }
  if (hash.flagContent != null) {
    options.flagContent = hash.flagContent;
  }
  if (hash.infoTabsDefaultTab != null) {
    infoTabsOptions.defaultTab = hash.infoTabsDefaultTab;
  }
  if (hash.infoTabsOpenByDefault != null) {
    infoTabsOptions.openByDefault = hash.infoTabsOpenByDefault;
  }
  if (hash.infoTabsDisabledTabs != null) {
    infoTabsOptions.disabledTabs = hash.infoTabsDisabledTabs;
  }
  return eventData;
}
